import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Tags } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, inputCls } from "@/components/admin/FormField";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import EmptyState from "@/components/admin/EmptyState";
import ImageUpload from "@/components/admin/ImageUpload";

const emptyCategory = { name_en: "", name_fr: "", image: "", order: 99 };

export default function Categories() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const load = () => api.get("/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await api.put(`/admin/categories/${editing.id}`, editing);
      else await api.post("/admin/categories", editing);
      toast.success("Category saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/categories/${deleteId}`);
      toast.success("Category deleted");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setDeleteId(null);
    load();
  };

  return (
    <div data-testid="admin-categories">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold text-navy">Categories</h1>
        <button onClick={() => setEditing({ ...emptyCategory })} className="btn-gold flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="add-category-btn">
          <Plus size={16} /> Add Category
        </button>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Tags} title="No categories yet" hint="Add your first category to organize products." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <div key={c.id} className="flex items-center gap-3 rounded-2xl border border-border bg-white p-4 card-shadow">
              {c.image ? (
                <img src={c.image} alt="" className="h-14 w-14 rounded-full object-cover" />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-cream text-navy/30"><Tags size={20} /></div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-head font-semibold text-navy">{c.name_en}</p>
                <p className="truncate text-xs text-muted-foreground">{c.name_fr}</p>
              </div>
              <button onClick={() => setEditing(c)} className="text-ocean" aria-label="Edit"><Pencil size={16} /></button>
              <button onClick={() => setDeleteId(c.id)} className="text-sunset" aria-label="Delete"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-navy">{editing?.id ? "Edit Category" : "New Category"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-3" data-testid="category-form">
              <Field label="Name (EN)"><input required value={editing.name_en} onChange={(e) => setEditing({ ...editing, name_en: e.target.value })} className={inputCls} /></Field>
              <Field label="Name (FR)"><input required value={editing.name_fr} onChange={(e) => setEditing({ ...editing, name_fr: e.target.value })} className={inputCls} /></Field>
              <Field label="Category Image">
                <ImageUpload
                  images={editing.image ? [editing.image] : []}
                  onChange={(images) => setEditing({ ...editing, image: images[0] || "" })}
                  single
                />
              </Field>
              <button className="btn-gold w-full rounded-full py-2.5 text-sm font-semibold">Save</button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(o) => !o && setDeleteId(null)}
        title="Delete this category?"
        description="This cannot be undone. Products already assigned to it will keep their category slug."
        onConfirm={confirmDelete}
      />
    </div>
  );
}
